import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import * as models from '../../../../models'
import { Value } from '../../../value'
import type { FnHelper } from '../helper'
import { Key } from '../key'

import { CreateObject } from './create-object'
import { GetAttribute } from './get-attribute'

/**
 * Retrieves the given record as an object
 *
 * @param {GetRecord.Options} options {@link GetRecord.Options}
 * @example
 * // Retrieve an instance of the query entity with partial IDs
 * { '@.*': {} }
 * // ->
 * { '!id': 'abc123', '!code': 'RM100', ... }
 *
 * // Retrieve an instance of the query entity with full IDs
 * { '@.*': { attributeIdFormat: 'full' } }
 * // ->
 * { 'attr:ent#!space#!room:!id': 'abc123', 'attr:ent#!space#!room:!code': 'RM100', ... }
 */
export interface GetRecord<out T = any> {
  /** {@link GetRecord} */
  readonly [Key.GetRecord]: T extends readonly unknown[]
    ? never
    : T extends Object.Constraint
      ? GetRecord.Options
      : never
}
export namespace GetRecord {
  export const isGetRecord: TypeChecker<GetRecord> = TypeChecker.Factory.lazy(
    () =>
      TypeChecker.Factory.object<GetRecord>({
        [Key.GetRecord]: Options.isOptions,
      }),
  ).usingAlias('Fn.GetRecord')
  export const assertIsGetRecord: TypeAsserter<GetRecord> =
    TypeAsserter.Factory.from(isGetRecord)

  export interface Options {
    /**
     * By default, the returned object uses the partial IDs of attributes as the
     * keys for those attributes. Set this to true to get full IDs instead of
     * partial IDs
     *
     * @default Id.Format.Partial
     */
    attributeIdFormat?: models.Id.Format
    /**
     * Set this to `true` to omit any reference attributes from the returned
     * object
     *
     * @default false
     */
    omitReferenceAttributes?: boolean
    /**
     * Applied to each attribute in the {@link Record}
     *
     * @see {@link GetAttribute.Options}
     */
    attributeOptions?: GetAttribute.Options
  }
  export namespace Options {
    export const isOptions = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.object<Options>({
        attributeIdFormat: TypeChecker.Factory.optional(
          models.Id.Format.isFormat,
        ),
        attributeOptions: TypeChecker.Factory.optional(
          GetAttribute.Options.isOptions,
        ),
        omitReferenceAttributes: TypeChecker.Factory.optional(
          TypeChecker.isBoolean,
        ),
      }),
    )

    export type WithDefaults = Required<Options>
    export namespace WithDefaults {
      /**
       * Returns a copy of the given {@link Options} that has any `undefined`
       * fields set to their default values
       */
      export function from({
        attributeIdFormat = models.Id.Format.Partial,
        attributeOptions = {},
        omitReferenceAttributes = false,
      }: Readonly.Deep<Options>): WithDefaults {
        return { attributeIdFormat, attributeOptions, omitReferenceAttributes }
      }
    }
  }

  export const HELPER: FnHelper<GetRecord> = {
    createNode: (fn, context) => {
      // TODO: more useful error message
      TypeAsserter.assertIsNotNullish(context.modelId)
      const { attributeIdFormat, attributeOptions, omitReferenceAttributes } =
        Options.WithDefaults.from(fn[Key.GetRecord])
      const useFullAttributeId = attributeIdFormat === models.Id.Format.Full
      const obj: Mutable<CreateObject.Node['arg']> = {}
      for (const attribute of context.schema.getAttributesOf(context.modelId)) {
        if (
          omitReferenceAttributes &&
          Value.Type.Reference.isReference(attribute.type)
        ) {
          // Skips reference attributes, if requested
          continue
        }
        const attributePartialId = models.Id.Parts.from(attribute).partialId
        obj[useFullAttributeId ? attribute.id : attributePartialId] =
          GetAttribute.HELPER.createNode(
            {
              [Key.GetAttribute]: [attributePartialId, attributeOptions],
            },
            context,
          )
      }
      return new CreateObject.Node(obj)
    },
    expressionsInArgs: fn => [],
    typeChecker: isGetRecord,
  }
}
